import React, { useEffect, useMemo, useState } from 'react';
import { FundraisingType, FundraisingTier } from '../../shared/models/Project';
import { logInfo, logError } from '../../utils/errorHandler';
import { useOrganization } from '../../contexts/OrganizationContext';
import './DonationModal.css';

interface DonationModalProps {
  visible: boolean;
  onClose: () => void;
  onConfirm?: (params: { amount: number; recurring: boolean; anonymous: boolean }) => void;
  onAgreementClick?: () => void;
  fundraisingType?: FundraisingType;
  tiers?: FundraisingTier[];
  supportCustomAmount?: boolean;
  customAmountMin?: number;
  customAmountMax?: number;
  projectTitle?: string;
}

const preset = [10, 50, 350];

export const DonationModal: React.FC<DonationModalProps> = ({
  visible,
  onClose,
  onConfirm,
  onAgreementClick,
  fundraisingType = FundraisingType.MONTHLY_AND_ONE_TIME,
  tiers = [],
  supportCustomAmount = true,
  customAmountMin = 0,
  customAmountMax = 0,
  projectTitle = '项目',
}) => {
  const { organization } = useOrganization();
  
  // 调试：检查 props
  useEffect(() => {
    if (visible) {
      logInfo('[DonationModal] Modal opened', {
        hasOnAgreementClick: !!onAgreementClick,
        hasDonationAgreement: !!organization?.donationAgreement,
        organizationId: organization?.id,
        organizationName: organization?.name,
        donationAgreementLength: organization?.donationAgreement?.length || 0
      });
    }
  }, [visible, onAgreementClick, organization]);
  const amounts = useMemo(() => {
    if (tiers && tiers.length > 0) {
      return tiers.map((t) => t.amount);
    }
    return preset;
  }, [tiers]);

  const initialAmount = useMemo(() => {
    return amounts.length > 0 ? amounts[0] : preset[0];
  }, [amounts]);

  const [amount, setAmount] = useState<number>(initialAmount);
  const [custom, setCustom] = useState<string>('');
  const [recurring, setRecurring] = useState<boolean>(fundraisingType !== FundraisingType.ONE_TIME);
  const [useCustom, setUseCustom] = useState<boolean>(false);
  const [anonymous, setAnonymous] = useState<boolean>(false);
  const [agreed, setAgreed] = useState<boolean>(false);

  // 获取当天的日期（1-31）
  const todayDate = new Date().getDate();

  const allowMonthly =
    fundraisingType === FundraisingType.MONTHLY_ONLY || fundraisingType === FundraisingType.MONTHLY_AND_ONE_TIME;
  const allowOneTime =
    fundraisingType === FundraisingType.ONE_TIME || fundraisingType === FundraisingType.MONTHLY_AND_ONE_TIME;

  // 锁定背景滚动
  useEffect(() => {
    if (visible) {
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prevOverflow;
      };
    }
  }, [visible]);

  // 每次打开重置
  useEffect(() => {
    if (visible) {
      setUseCustom(false);
      setCustom('');
      setAnonymous(false);
      setAgreed(false);
      setAmount(amounts.length > 0 ? amounts[0] : preset[0]);
      setRecurring(fundraisingType !== FundraisingType.ONE_TIME);
    }
  }, [visible, amounts, fundraisingType]);

  if (!visible) return null;

  const handleConfirm = () => {
    logInfo('[DonationModal] handleConfirm called', {
      amount: useCustom ? Number(custom) : amount,
      useCustom,
      custom,
      recurring,
      anonymous,
      agreed
    });

    const val = useCustom ? Number(custom) : amount;
    if (!val || Number.isNaN(val) || val <= 0) {
      alert('请输入有效金额');
      return;
    }
    if (customAmountMin > 0 && val < customAmountMin) {
      alert(`金额需不小于 ${customAmountMin} 元`);
      return;
    }
    if (customAmountMax > 0 && val > customAmountMax) {
      alert(`金额需不大于 ${customAmountMax} 元`);
      return;
    }
    if (!allowMonthly && recurring) {
      setRecurring(false);
    }
    if (!agreed) {
      alert('请先勾选已阅读并同意《捐赠协议》');
      return;
    }
    
    logInfo('[DonationModal] Calling onConfirm callback', {
      amount: val,
      recurring,
      anonymous,
      hasOnConfirm: !!onConfirm
    });
    onConfirm?.({ amount: val, recurring, anonymous });
    
    logInfo('[DonationModal] Closing modal');
    onClose();
  };

  return (
    <div className="donation-modal-mask" onClick={onClose}>
      <div className="donation-modal" onClick={(e) => e.stopPropagation()}>
        <div className="dm-header">选择捐赠金额</div>
        <div className="dm-amounts">
          {amounts.map((p) => (
            <button
              key={p}
              className={`dm-chip ${!useCustom && amount === p ? 'active' : ''} ${useCustom ? 'disabled' : ''}`}
              onClick={() => {
                setUseCustom(false);
                setCustom('');
                setAmount(p);
              }}
            >
              {p}元
            </button>
          ))}
        </div>
        {supportCustomAmount && (
          <input
            className="dm-input"
            placeholder="自定义金额"
            value={custom}
            onFocus={() => setUseCustom(true)}
            onChange={(e) => setCustom(e.target.value)}
          />
        )}

        <div className="dm-section-title">捐款方式</div>
        <div className="dm-tabs">
          {allowMonthly && (
            <button className={`dm-tab ${recurring ? 'active' : ''}`} onClick={() => setRecurring(true)}>
              每月一捐
            </button>
          )}
          {allowOneTime && (
            <button className={`dm-tab ${!recurring ? 'active' : ''}`} onClick={() => setRecurring(false)}>
              单次捐
            </button>
          )}
        </div>

        <div className="dm-actions">
          <div className="dm-checkboxes">
            <div className="dm-switch-row">
              <label className="dm-switch">
                <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} />
                <span className="dm-switch-slider"></span>
              </label>
              <span>匿名捐赠（不公开头像和昵称）</span>
            </div>
            <div className="dm-checkbox" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} style={{ marginRight: '6px' }} />
                <span>我已阅读并同意</span>
            </label>
              {(() => {
                // 调试：检查条件
                const hasDonationAgreement = !!organization?.donationAgreement;
                const hasOnAgreementClick = !!onAgreementClick;
                logInfo('[DonationModal] Rendering agreement link', {
                  hasDonationAgreement,
                  hasOnAgreementClick,
                  donationAgreement: organization?.donationAgreement,
                  organizationId: organization?.id,
                  organizationName: organization?.name
                });
                
                // 如果有 onAgreementClick，就显示可点击的链接（即使没有 donationAgreement 也显示，方便测试）
                if (hasOnAgreementClick) {
                  return (
                    <span 
                      className="dm-agreement-link" 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        logInfo('[DonationModal] Agreement link clicked', {
                          hasDonationAgreement: !!organization?.donationAgreement,
                          hasOnAgreementClick: !!onAgreementClick
                        });
                        // 跳转到协议页面，不关闭 modal，这样返回后可以继续完成捐赠
                        if (onAgreementClick) {
                          logInfo('[DonationModal] Calling onAgreementClick');
                          onAgreementClick();
                        } else {
                          logError('[DonationModal] onAgreementClick is undefined');
                        }
                      }}
                      onMouseDown={(e) => {
                        // 备用事件处理，确保点击能被捕获
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      style={{ marginLeft: '2px' }}
                    >
                      《捐赠协议》
                    </span>
                  );
                } else {
                  return <span style={{ marginLeft: '2px' }}>《捐赠协议》</span>;
                }
              })()}
            </div>
          </div>
          {recurring && (
            <div className="dm-tip">
              开通《{projectTitle}》每月一捐，支付成功后，后续每月{todayDate}号将自动扣款；每次扣款会有通知，可随时取消。
            </div>
          )}
          <button className="dm-primary" onClick={handleConfirm}>
            我要捐赠（{useCustom && custom.trim() ? custom : amount}元{recurring ? '/月' : ''}）
          </button>
        </div>
      </div>
    </div>
  );
};


